import {
  type Auth0Plugin,
  authGuard,
  createAuth0,
  type LogoutOptions,
  type RedirectLoginOptions,
} from "@auth0/auth0-vue";
import { type App, type Ref } from "vue";
import type {
  Auth0Config,
  AuthService,
  AuthState,
  AuthUser,
} from "./authService";
import type { RouteLocationNormalized } from "vue-router";

export class Auth0Adapter implements AuthService {
  private client: Auth0Plugin;
  private callbackUrl: string;
  private unguardedPaths: string[];

  constructor({ auth0Config }: { auth0Config: Auth0Config }) {
    this.callbackUrl = auth0Config.callbackUrl || "/callback";
    this.unguardedPaths = [this.callbackUrl];

    this.client = createAuth0({
      domain: auth0Config.domain,
      clientId: auth0Config.clientId,
      cacheLocation: "localstorage",
      useRefreshTokens: true,
      authorizationParams: {
        redirect_uri: `${window.location.origin}${this.callbackUrl}`,
        audience: auth0Config.audience,
      },
    });
  }

  async getAccessToken(): Promise<string | undefined> {
    try {
      return await this.client.getAccessTokenSilently();
    } catch (error) {
      console.error("Failed to get access token:", error);
      await this.logout();
      return undefined;
    }
  }

  async login(returnTo?: string): Promise<void> {
    const options: RedirectLoginOptions = {};
    if (returnTo) {
      options.appState = { returnTo };
    }
    await this.client.loginWithRedirect(options);
  }

  async logout(returnTo?: string): Promise<void> {
    const options: LogoutOptions = {
      logoutParams: {
        returnTo: `${window.location.origin}${returnTo || "/"}`,
      },
    };
    await this.client.logout(options);
  }

  getState(): AuthState {
    return {
      isAuthenticated: this.client.isAuthenticated as Ref<boolean>,
      isLoading: this.client.isLoading as Ref<boolean>,
      user: this.client.user as Ref<AuthUser | undefined>,
      error: this.client.error as Ref<Error | undefined>,
    };
  }

  shouldGuardRoute(path: string): boolean {
    return !this.unguardedPaths.includes(path);
  }

  async handleCallback(url?: string): Promise<void> {
    await this.client.handleRedirectCallback(url);
  }

  async checkSession(): Promise<void> {
    await this.client.checkSession();
  }

  async authGuard(to: RouteLocationNormalized): Promise<boolean> {
    if (!this.shouldGuardRoute(to.path)) {
      return true;
    }

    return await authGuard(to);
  }

  install(app: App): void {
    app.use(this.client);
  }

  requiresRealAuthEndpoints(): boolean {
    return true;
  }
}
